import { useNftx } from '@nftx/react';
import { Vault } from 'nftx.js';
import { useState } from 'react';
import ActionItem from './ActionItem';
import useProcessTokens from '@/application/write/swap/useProcessTokens';
import { useTranslations } from 'use-intl';

export type EligibilityState =
  | 'UNKNOWN'
  | 'REQUIRES_PROCESSING'
  | 'ELIGIBLE'
  | 'NOT_ELIGIBLE';

type Props = {
  index: number;
  symbol: string;
  tokenIds: `${number}`[];
  vault: Vault;
  eligibilityStates: EligibilityState[];
  complete: boolean;
};

const ProcessTokensActionItem = ({
  index,
  symbol,
  tokenIds,
  vault,
  eligibilityStates,
  complete,
}: Props) => {
  const t = useTranslations();
  const {
    core: { checkEligible },
    provider,
  } = useNftx();
  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<any>();
  const [processTokens, meta] = useProcessTokens({ vault });
  const outstandingTokenIds = tokenIds.filter(
    (tokenId, i) => eligibilityStates[i] === 'REQUIRES_PROCESSING'
  );
  const error = meta.error || eligibilityError;
  const status = (() => {
    // We don't want to trigger a success in the action item until we've checked that the tokens are now all eligible
    if (meta.status === 'Success' && !hasCheckedEligibility) {
      return 'Mining';
    }
    if (outstandingTokenIds.length === 0) {
      return 'Success';
    }
    return meta.status;
  })();

  const action = async () => {
    if (outstandingTokenIds.length) {
      await processTokens({ tokenIds: outstandingTokenIds });
    }
    const eligible = await checkEligible({
      provider,
      tokenIds,
      vault,
    });
    const allEligible = eligible.every((e) => e.eligible);
    if (allEligible) {
      setHasCheckedEligibility(true);
    } else {
      setEligibilityError(
        new Error(
          'Some of the items in your basket are not eligible for staking in this pool'
        )
      );
    }
  };

  // TODO: add intl for the processTokens text
  const label = complete ? (
    <span className="text-tertiary-green">{t('App.hello')}</span>
  ) : (
    t('App.hello')
  );

  return (
    <ActionItem
      index={index}
      label={label}
      error={error}
      status={status}
      action={action}
    />
  );
};

export default ProcessTokensActionItem;
